import React from 'react';
import Alert from '../../../../components/Alert';
import Toast from '../../../../components/Toast';
import JobApplicationCard from '../components/JobApplicationsCard';
import { useJobApplicationsViewModel } from '../viewModel/useJobApplicationsViewModel';
import TablePagination from '../../../company/JobManagement/Components/TablePagination';

const STATUS_CONFIG = [
  { key: '', label: 'All', icon: '📄' },
  { key: 'applied', label: 'Applied', icon: '📋' },
  { key: 'screening', label: 'Screening', icon: '🔍' },
  { key: 'interviewing', label: 'Interviewing', icon: '💬' },
  { key: 'offered', label: 'Offered', icon: '🎉' },
  { key: 'hired', label: 'Hired', icon: '✅' },
  { key: 'rejected', label: 'Rejected', icon: '❌' },
  { key: 'withdrawn', label: 'Withdrawn', icon: '↩️' },
];

const JobApplications: React.FC = () => {
  const {
    state,
    setStatusFilter,
    openDetails,
    hideToast,
    setCurrentPage,
    setViewMode,
    setRowsPerPage,
  } = useJobApplicationsViewModel();

  // Calculate counts for each simplified status from backend summary
  const calculateStatusCounts = () => {
    const counts: Record<string, number> = {};

    // Initialize all simplified statuses
    STATUS_CONFIG.forEach(({ key }) => {
      if (key) counts[key] = 0;
    });

    // Use backend statusSummary from view model (already converted)
    Object.entries(state.statusSummary).forEach(([status, count]) => {
      counts[status] = count;
    });

    return counts;
  };

  const statusCounts = calculateStatusCounts();

  // Calculate TOTAL from ALL statuses (for the "All" card)
  const calculateTotalFromAllStatuses = () => {
    let total = 0;
    Object.values(state.statusSummary).forEach(count => {
      total += count;
    });
    return total;
  };

  const totalAllStatuses = calculateTotalFromAllStatuses();

  return (
    <div className="p-3 sm:p-4 md:p-6">
      {/* Status Summary Cards - Responsive Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-4 lg:grid-cols-8 gap-2 sm:gap-3 mb-6 sm:mb-8">
        {STATUS_CONFIG.map(({ key, label, icon }) => (
          <div
            key={key || 'all'}
            onClick={() => setStatusFilter(key)}
            className={`cursor-pointer bg-white p-2 sm:p-3 rounded-lg border transition-all hover:shadow-sm ${state.statusFilter === key
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200'
              }`}
          >
            <div className="text-base sm:text-xl font-bold mb-0.5 sm:mb-1">
              {key
                ? statusCounts[key] || 0
                : totalAllStatuses}
            </div>
            <div className="text-[10px] sm:text-xs text-gray-600 flex items-center gap-0.5 sm:gap-1">
              <span className="text-xs sm:text-sm">{icon}</span>
              <span className="truncate">{label}</span>
            </div>
          </div>
        ))}
      </div>

      {state.error && <Alert type="error" message={state.error} onClose={() => { }} />}

      {/* ====== HEADER SECTION ====== */}
      <div className="flex justify-end flex-col md:flex-row md:items-center gap-4 mb-6 sm:mb-8">
        {/* Right side - View Toggle - Hidden on mobile */}
          <div className="hidden sm:flex items-center space-x-2">
              <span className="text-sm text-gray-500">View:</span>
              <div className="flex rounded-lg border border-gray-300">
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1 text-sm font-medium rounded-l-lg ${state.viewMode === 'list'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-1 text-sm font-medium rounded-r-lg ${state.viewMode === 'grid'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
              </div>
            </div>
      </div>
      {/* ====== END HEADER ====== */}

      {state.loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={`skeleton-${index}`} className="card p-4 animate-pulse">
              {/* Skeleton content */}
              <div className="h-4 bg-secondary-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-secondary-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {state.filteredApplications.length > 0 ? (
            <>
              <JobApplicationCard
                applications={state.filteredApplications}
                onViewDetails={openDetails}
                viewMode={state.viewMode} // Pass viewMode to component
              />

              {/* Add Pagination - Responsive */}
              {state.totalPages > 1 && (
                      <TablePagination
                        currentPage={state.currentPage}
                        totalCount={state.totalApplications}
                        rowsPerPage={state.applicationsPerPage}
                        onPageChange={(page) => setCurrentPage(page)}
                        onRowsPerPageChange={(rows) => {
                          setRowsPerPage(rows);
                        }}
                        isGrid={state.viewMode === 'grid'}
                      />
                    )}
            </>
          ) : (
            <div className="text-center py-8 sm:py-12 px-4">
              <div className="text-gray-400 text-4xl sm:text-6xl mb-4">📄</div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                No applications found
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 max-w-md mx-auto">
                {state.statusFilter
                  ? `You don't have any applications in the "${state.statusFilter}" status`
                  : 'You haven\'t applied to any jobs yet'}
              </p>
              {state.statusFilter && (
                <button
                  onClick={() => setStatusFilter('')}
                  className="px-4 sm:px-6 py-2 bg-blue-600 text-white text-sm sm:text-base rounded-lg hover:bg-blue-700 transition-colors w-full sm:w-auto"
                >
                  View All Applications
                </button>
              )}
            </div>
          )}
        </>
      )}

      {state.toast && (
        <Toast
          message={state.toast.message}
          type={state.toast.type}
          isVisible
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default JobApplications;